import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Une clases de Tailwind resolviendo conflictos (la última gana). */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Formatea centavos de peso como moneda colombiana: 1250000 → "$12.500". */
export function formatCOP(cents: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(cents / 100));
}

/** Formatea distancias: 850 → "850 m", 3400 → "3,4 km". */
export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toLocaleString('es-CO', { maximumFractionDigits: 1 })} km`;
}

/** Fecha corta con hora: "12 ago, 7:35 p. m." */
export function formatFecha(iso: string): string {
  return new Date(iso).toLocaleString('es-CO', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Solo la hora: "7:35 p. m." */
export function formatHora(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-CO', { hour: 'numeric', minute: '2-digit' });
}

/** Cuánto tiempo lleva esperando algo: "hace 12 min". */
export function desdeHace(iso: string): string {
  const minutos = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutos < 1) return 'recién';
  if (minutos < 60) return `hace ${minutos} min`;
  return `hace ${Math.floor(minutos / 60)} h`;
}

/**
 * Deja un texto listo para comparar sin que estorben tildes ni mayúsculas.
 *
 * `NFD` separa cada letra acentuada en letra + tilde, y el reemplazo borra las
 * tildes sueltas: «Múnera» queda «munera» y se encuentra escribiendo «munera»,
 * «MUNERA» o «Múnera». Lo mismo con la eñe, que en la práctica es lo que se
 * quiere al buscar: quien escribe «nino» está buscando «Niño».
 *
 * Es lo que separa un buscador que sirve de uno que obliga a escribir el
 * nombre exactamente como está en la base.
 */
export function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

/**
 * ¿Alguno de estos campos contiene lo que se busca?
 *
 * Cada palabra del término tiene que aparecer en algún sitio, no la frase
 * entera seguida: así «sabor 65» encuentra un pedido de Sabor Paisa a la
 * Carrera 65, que es como busca la gente cuando recuerda dos datos sueltos.
 */
export function coincide(termino: string, ...campos: (string | null | undefined)[]): boolean {
  const palabras = normalizar(termino).split(/\s+/).filter(Boolean);
  if (palabras.length === 0) return true;

  const heno = campos.filter(Boolean).map((c) => normalizar(c!)).join(' ');
  return palabras.every((p) => heno.includes(p));
}
